import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 h-[70px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/76137be8-9162-457b-932a-27f45f86284c.png" alt="Sermaru Logo" className="h-10 w-10 object-contain" />
            <h1 className="text-lg font-bold text-gray-900">Sermaru, S.A.</h1>
          </div>
          <div className="flex items-center gap-8">
            <button onClick={() => scrollToSection('products')} className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Products</button>
            <button onClick={() => scrollToSection('about')} className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">About</button>
            <button onClick={() => scrollToSection('contact')} className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-[70px] h-[600px] bg-gradient-to-r from-purple-50 via-indigo-50 to-emerald-50 flex items-center">
        <div className="max-w-7xl mx-auto px-20 w-full">
          <div className="max-w-2xl">
            <h2 className="text-6xl font-serif font-bold text-gray-900 leading-tight mb-6">
              Secure &amp; Clean<br />
              iOS Solutions<br />
              for Modern Users
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Expert team developing VPN and Cleaner applications to enhance security and optimize your mobile experience
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="bg-[#FAFAF8] py-28">
        <div className="max-w-7xl mx-auto px-8">
          <h3 className="text-5xl font-bold text-center text-gray-900 mb-16">Our Products</h3>
          <div className="flex gap-8 items-start">
            <div className="w-[45%] h-[480px] rounded-3xl bg-gradient-to-br from-purple-700 to-blue-600 p-10 flex flex-col justify-end relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 mb-6 flex items-center justify-center">
                  <i className="ri-shield-check-line text-5xl text-white"></i>
                </div>
                <h4 className="text-3xl font-bold text-white mb-3">VPN Shield</h4>
                <p className="text-white/80 text-base leading-relaxed">
                  Advanced VPN protection for your iOS device. Browse securely, protect your privacy, and access content from anywhere with military-grade encryption.
                </p>
              </div>
            </div>
            <div className="w-[50%] h-[480px] rounded-3xl bg-gradient-to-br from-pink-500 to-orange-500 p-10 flex flex-col justify-end relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 mb-6 flex items-center justify-center">
                  <i className="ri-brush-line text-5xl text-white"></i>
                </div>
                <h4 className="text-3xl font-bold text-white mb-3">Smart Cleaner</h4>
                <p className="text-white/80 text-base leading-relaxed">
                  Optimize your device performance with intelligent cleaning. Remove junk files, manage storage, and keep your iOS device running smoothly and efficiently.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex gap-16 items-start">
            <div className="w-[35%]">
              <p className="text-sm text-gray-500 mb-8">/about-us</p>
              <div className="flex items-center gap-3 mt-32">
                <div className="flex -space-x-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 border-2 border-white"></div>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-500 to-gray-700 border-2 border-white"></div>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 border-2 border-white"></div>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-500 to-gray-700 border-2 border-white"></div>
                </div>
                <p className="text-sm text-gray-800 font-medium">Expert Team</p>
              </div>
            </div>
            <div className="w-[60%]">
              <p className="text-lg text-gray-800 leading-relaxed font-light">
                Sermaru, S.A. is a dedicated team of experts specializing in developing modern applications for the iOS platform. Our mission is to create innovative solutions that enhance user experience and improve the security of mobile devices. With years of experience in mobile app development, we focus on delivering high-quality products that meet the evolving needs of iOS users worldwide. Our flagship products, VPN Shield and Smart Cleaner, represent our commitment to excellence in mobile security and device optimization. We combine cutting-edge technology with user-friendly design to ensure that every application we create provides real value to our users. Our team continuously researches and implements the latest security protocols and optimization techniques to stay ahead in the rapidly changing mobile landscape.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Showcase Section */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex justify-between items-start mb-20">
            <p className="text-sm text-gray-500">/showcase</p>
            <div className="max-w-2xl">
              <h3 className="text-5xl font-light text-gray-900 leading-relaxed">
                We develop applications<br />
                with focus on security,<br />
                performance, and<br />
                exceptional user experience
              </h3>
            </div>
          </div>
          <div className="flex gap-8">
            <div className="w-[48%] h-[520px] rounded-2xl overflow-hidden shadow-lg relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/915197aaeace13d2eaf16e12c3c25afb.jpg" 
                alt="VPN Application Interface" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-6 left-6 bg-black/70 backdrop-blur-sm px-6 py-3 rounded-lg">
                <p className="text-white text-sm font-medium">Security First Approach</p>
              </div>
            </div>
            <div className="w-[48%] flex flex-col gap-8">
              <div className="h-[245px] rounded-2xl bg-emerald-50 shadow-lg overflow-hidden relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20device%20performance%20optimization%20concept%20with%20clean%20storage%20visualization%20and%20speed%20indicators%2C%20soft%20green%20background%2C%20minimalist%20modern%20design%2C%20professional%20digital%20illustration%20showing%20efficiency%20and%20cleanliness&width=700&height=350&seq=showcase2&orientation=landscape" 
                  alt="Performance Optimization" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="h-[245px] rounded-2xl bg-pink-50 shadow-lg overflow-hidden relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=user-friendly%20mobile%20app%20interface%20design%20showing%20intuitive%20controls%20and%20elegant%20UI%20elements%2C%20soft%20pink%20background%2C%20modern%20minimalist%20style%2C%20professional%20product%20design%20illustration%20emphasizing%20ease%20of%20use&width=700&height=350&seq=showcase3&orientation=landscape" 
                  alt="User Experience Design" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-32">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h3 className="text-5xl font-bold text-gray-900 mb-6">Ready to Enhance Your iOS Experience</h3>
          <p className="text-lg text-gray-600 mb-12">Join thousands of users trusting our applications</p>
          <div className="inline-flex items-center gap-3 bg-[#4A3728] text-white px-8 py-4 rounded-full text-base font-medium">
            <span>Explore Our Solutions</span>
            <i className="ri-arrow-right-line text-xl"></i>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <footer id="contact" className="bg-white px-5 pb-5">
        <div className="bg-[#0F172A] rounded-3xl p-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between mb-16">
              <div className="w-[30%]">
                <div className="flex items-center gap-3 mb-4">
                  <img src="https://public.readdy.ai/ai/img_res/76137be8-9162-457b-932a-27f45f86284c.png" alt="Sermaru Logo" className="h-10 w-10 object-contain" />
                  <h4 className="text-3xl font-bold text-white">Sermaru, S.A.</h4>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Developing modern iOS applications for enhanced security and performance.
                </p>
              </div>
              <div className="w-[20%]">
                <h5 className="text-white font-medium mb-5">Location</h5>
                <div className="space-y-2 text-sm text-gray-400">
                  <p>PRINCIPAL 12</p>
                  <p>San Miguelito</p>
                  <p>0819</p>
                  <p>Panama</p>
                </div>
              </div>
              <div className="w-[25%]">
                <h5 className="text-white font-medium mb-5">Get in Touch</h5>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <i className="ri-mail-line text-base"></i>
                    <span>information@sermarusa.com</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <i className="ri-phone-line text-base"></i>
                    <span>+1 6508440180</span>
                  </div>
                </div>
              </div>
              <div className="w-[20%]">
                <h5 className="text-white font-medium mb-3">Stay Updated</h5>
                <p className="text-white/70 text-sm mb-6">Subscribe to our newsletter</p>
                <div className="relative">
                  <input 
                    type="email" 
                    placeholder="Your email" 
                    className="w-full bg-transparent border border-white/30 rounded-full px-5 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-white/50"
                  />
                  <button className="absolute right-1 top-1 w-10 h-10 bg-white rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors">
                    <i className="ri-arrow-right-line text-gray-900"></i>
                  </button>
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-8 flex justify-between items-center">
              <p className="text-gray-500 text-sm">© 2025 Sermaru, S.A. All rights reserved.</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 text-sm hover:text-gray-400 transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
